/**
 * Find a signatory's signature anchor in the rendered .docx.
 *
 * The contract stores, per signatory, the text-context anchor captured when the
 * template was marked: `originalText` plus `contextBefore`/`contextAfter` and a
 * `paragraphIndex`. Turning that back into a rectangle is what places the
 * signature box in the review page.
 *
 * The naive approach — search the whole rendered document for `originalText` —
 * fails exactly where it matters. `locateInBody` never fails on ambiguity: it
 * scores candidates by context and takes the best even on a tie. Signature
 * lines are boilerplate ("Signature: ______"), and each signatory's surrounding
 * context is usually the *same* boilerplate, so the scores tie and the winner is
 * arbitrary. One signatory's box lands on another's line, often a page away.
 *
 * `paragraphIndex` is what distinguishes them, and for a signature anchor it is
 * directly usable: signature sub-fields are never tokenized, so the persisted
 * value is the marking-time one, computed over the rendered DOM as
 * `paragraphs.indexOf(p)` with the same `isExcludedFromBody` filter used here,
 * on a docx-preview render configured identically to this page. It indexes the
 * very list `bodyParagraphs()` returns.
 *
 * It is still only a hint — a merge or an edit can add or remove paragraphs —
 * so every tier verifies that the paragraph it picked actually contains the
 * anchor text before committing to it.
 */

import {
  buildBodyModel,
  isExcludedFromBody,
  locateInBody,
  normalize,
  rangeFromBodyOffsets,
  type BodyModel,
} from '@/modules/org/components/docx-builder/selection/body-model';
import { buildTextModel, loadDocxDocument, locate, type TextModel } from '@/lib/docx/tokenize';

/** The persisted anchor, structurally — matches `ContractSignatureAnchor`. */
export interface DocxAnchorRef {
  originalText: string;
  contextBefore?: string;
  contextAfter?: string;
  paragraphIndex?: number | null;
}

/** Which strategy produced a hit. Surfaced for dev logging, not behaviour. */
export type AnchorTier = 'index' | 'scan' | 'xml' | 'document';

/** Viewport-space bounds of the anchor's opening line. */
export interface AnchorRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface AnchorHit {
  range: Range;
  rect: AnchorRect;
  tier: AnchorTier;
  /** The paragraph actually used; null when resolved document-wide. */
  paragraphIndex: number | null;
}

export type AnchorResolver = (anchor: DocxAnchorRef) => AnchorHit | null;

/** How far either side of the stored index to look when it has drifted. */
const SCAN_RADIUS = 8;

/**
 * The rendered body paragraphs, in the same order and with the same exclusions
 * the marking canvas used when it recorded `paragraphIndex`.
 */
export function bodyParagraphs(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll('p')).filter((p) => !isExcludedFromBody(p, root));
}

/**
 * The anchor's bounds on its OPENING line.
 *
 * A range that wraps reports one client rect per line; unioning all of them
 * would give a tall block spanning both, and the signature box would straddle
 * the wrap. Keeping the first line matches how the send-time detector unions
 * only the covering text items of a single run of glyphs.
 */
function firstLineRect(range: Range): AnchorRect | null {
  const rects = Array.from(range.getClientRects()).filter((r) => r.width > 0 && r.height > 0);
  if (rects.length === 0) {
    const r = range.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return null;
    return { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
  }

  const minTop = Math.min(...rects.map((r) => r.top));
  const firstLine = rects.filter((r) => Math.abs(r.top - minTop) < r.height / 2);
  return {
    left: Math.min(...firstLine.map((r) => r.left)),
    top: minTop,
    right: Math.max(...firstLine.map((r) => r.right)),
    bottom: Math.max(...firstLine.map((r) => r.bottom)),
  };
}

/** Resolve the anchor within one already-chosen container. */
function hitIn(
  container: HTMLElement,
  anchor: DocxAnchorRef,
  tier: AnchorTier,
  paragraphIndex: number | null,
  model?: BodyModel
): AnchorHit | null {
  const bodyModel = model ?? buildBodyModel(container);
  const loc = locateInBody(
    bodyModel,
    anchor.originalText,
    anchor.contextBefore ?? '',
    anchor.contextAfter ?? ''
  );
  if (!loc) return null;
  const range = rangeFromBodyOffsets(bodyModel, loc.rawStart, loc.rawEnd);
  if (!range) return null;
  const rect = firstLineRect(range);
  if (!rect) return null;
  return { range, rect, tier, paragraphIndex };
}

/**
 * Build a resolver for one render pass.
 *
 * Shared work is done once and lazily: the paragraph list is cheap and built up
 * front, while the whole-document body model and the .docx XML model are only
 * constructed if some anchor actually falls through to the tier that needs them
 * (the XML tier costs a zip inflate plus a full XML parse).
 */
export function createAnchorResolver(
  root: HTMLElement,
  docxBytes?: Uint8Array | null
): AnchorResolver {
  const paragraphs = bodyParagraphs(root);

  let documentModel: BodyModel | null = null;
  const getDocumentModel = () => (documentModel ??= buildBodyModel(root));

  // undefined = not attempted yet, null = unavailable or failed to parse.
  let xmlModel: TextModel | null | undefined;
  const getXmlModel = (): TextModel | null => {
    if (xmlModel === undefined) {
      try {
        // Copy the bytes: the caller keeps using them for rendering and edits.
        xmlModel = docxBytes
          ? buildTextModel(loadDocxDocument(new Uint8Array(docxBytes)).doc)
          : null;
      } catch {
        xmlModel = null;
      }
    }
    return xmlModel;
  };

  /** Which `w:p` holds this anchor, per the .docx itself. */
  const xmlParagraphIndex = (anchor: DocxAnchorRef): number | null => {
    const model = getXmlModel();
    if (!model) return null;
    const located = locate(model, {
      name: 'signature_anchor',
      selectedText: anchor.originalText,
      contextBefore: anchor.contextBefore,
      contextAfter: anchor.contextAfter,
      paragraphIndex: anchor.paragraphIndex ?? null,
    });
    if ('error' in located) return null;
    const seg = model.segments.find(
      (s) => s.node && s.vStart <= located.rawStart && s.vStart + s.text.length > located.rawStart
    );
    return seg ? seg.paragraphIndex : null;
  };

  return (anchor) => {
    const needle = normalize((anchor.originalText ?? '').trim()).norm;
    if (!needle) return null;

    const contains = (i: number) => {
      const el = paragraphs[i];
      return !!el && normalize(el.textContent ?? '').norm.includes(needle);
    };

    // 1. The stored index, verified.
    const stored = anchor.paragraphIndex;
    if (typeof stored === 'number' && contains(stored)) {
      const hit = hitIn(paragraphs[stored], anchor, 'index', stored);
      if (hit) return hit;
    }

    // 2. The index drifted — a merge or edit changed the paragraph count. Walk
    //    outward so the nearest candidate wins.
    if (typeof stored === 'number') {
      for (let d = 1; d <= SCAN_RADIUS; d++) {
        for (const i of [stored - d, stored + d]) {
          if (i < 0 || i >= paragraphs.length || !contains(i)) continue;
          const hit = hitIn(paragraphs[i], anchor, 'scan', i);
          if (hit) return hit;
        }
      }
    }

    // 3. No usable index — ask the .docx which occurrence is the real one.
    const fromXml = xmlParagraphIndex(anchor);
    if (fromXml !== null && contains(fromXml)) {
      const hit = hitIn(paragraphs[fromXml], anchor, 'xml', fromXml);
      if (hit) return hit;
    }

    // 4. Document-wide. Ambiguous where the anchor repeats — which is the whole
    //    reason for the tiers above — but a plausible box still beats dropping
    //    the signatory to a default placement on the last page.
    return hitIn(root, anchor, 'document', null, getDocumentModel());
  };
}
